import { Component, DestroyRef, forwardRef, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { IonTextarea } from '@ionic/angular';
import { tap } from 'rxjs';

@Component({
  selector: 'nty-textarea',
  imports: [ReactiveFormsModule, IonTextarea],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextareaComponent), multi: true }],
  template: `
    <ion-textarea
      class="custom"
      [formControl]="ctrl"
      [required]="required"
      [errorText]="errorText"
      [autoGrow]="autoGrow"
      [rows]="rows"
      [counter]="counter"
      [minlength]="1"
      [maxlength]="maxLength"
      [label]="label"
      [helperText]="helperText"
      [labelPlacement]="labelPlacement"
      [placeholder]="placeholder" />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    @use 'terra';

    ion-textarea.custom {
      --background: #{terra.color(background)};
      --border-radius: #{terra.radius(large)};
      --border-color: transparent;
      --padding-top: 16px;
      --padding-end: 16px;
      --padding-bottom: 16px;
      --padding-start: 16px;
      --highlight-color: #{terra.color(ninety)};

      &.has-focus {
        &::ng-deep {
          label {
            outline: solid 1px terra.color(ninety);
            color: terra.color(text);
          }
        }

        &.ion-invalid:not(.ion-pristine) {
          &::ng-deep {
            label {
              outline: solid 1px terra.color(red);
            }
          }
        }
      }

      &::ng-deep {
        .textarea-bottom {
          border-color: transparent;
        }

        label {
          border: solid 1px terra.color(border);
          color: terra.color(text, lightest);
        }

        textarea {
          color: terra.color(text);
        }
      }
    }
  `,
})
export class TextareaComponent implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);

  @Input() label?: string;
  @Input() labelPlacement: 'end' | 'fixed' | 'floating' | 'stacked' | 'start' = 'floating';
  @Input() placeholder?: string;
  @Input() helperText?: string;
  @Input() rows = 3;
  @Input() autoGrow = false;
  @Input() counter = true;
  @Input() minLength = 1;
  @Input() maxLength = 2000;
  @Input() required = true;
  @Input() errorText?: string;

  ctrl = new FormControl('', { nonNullable: true });

  ngOnInit(): void {
    this.ctrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(val => {
          if (this.onChangeFn) this.onChangeFn(val);
          if (this.onTouchFn) this.onTouchFn();
        })
      )
      .subscribe();
  }

  writeValue(val: unknown): void {
    if (typeof val === 'string') {
      this.ctrl.patchValue(val);
    }
  }

  private onChangeFn: ((val: string) => void) | null = null;
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  private onTouchFn: (() => void) | null = null;
  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    return isDisabled ? this.ctrl.disable() : this.ctrl.enable();
  }
}
